import { useCallback, useMemo } from "react";
import { t } from "i18next";
import { isEmpty } from "lodash";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";

import { DRAWER_HEIGHT } from "@constants/Drawer";
import { EventType } from "@enums/component.enum";
import { slotType } from "@enums/hooks.enum";
import { DateFormats } from "@enums/shared.enum";
import {
  DateSelectArg,
  EventContentArg,
  EventDropArg,
} from "@fullcalendar/core";
import { EventResizeDoneArg } from "@fullcalendar/interaction/index.js";
import { useBooking } from "@hooks/useBooking/useBooking";
import { usePricing } from "@hooks/usePricing/usePricing";
import {
  BookingFetchResponseType,
  ResourceType,
  SingleSlot,
} from "@models/services/booking";
import bookingSlice from "@slices/booking.slice";
import drawerSlice from "@slices/drawer.slice";
import { RootState } from "@store/index";
import {
  adjustStartTimeFromTimeMaps,
  checkQuotaExceeded,
  getDurationConfig,
  validateAdvancedBooking,
  validateConsecutiveSlots,
} from "@utils/booking.utils";
import { getMinutesDiff } from "@utils/common.utils";

export const useBookingCalendarEvents = (
  slots: BookingFetchResponseType,
  setHasError: (hasError: boolean) => void,
  setIsLoading: (isLoading: boolean) => void
) => {
  const { getSelectedResource } = useBooking();
  const dispatch = useDispatch();

  const selectedDate = useSelector(
    (State: RootState) => State.booking.selectedDate
  );

  const timeMaps = useSelector((State: RootState) => State.booking.timeMaps);

  const { fetchQuotaInfo } = usePricing();

  const updateSelectedSlot = useCallback(
    async (
      startTime: string,
      endTime: string,
      resourceId: string,
      minutesDiff: number
    ) => {
      const selectResource: ResourceType = getSelectedResource(
        slots,
        resourceId
      );

      if (!selectResource || isEmpty(selectResource)) {
        setHasError(true);
        setIsLoading(false);
        return false;
      }

      const { allowedDurations, availableSlots, occupiedSlots } =
        selectResource.slotInfo.slots;

      const flatTimeMaps: string[] = timeMaps.flat().map(item => item.time);

      let updatedStartTime = adjustStartTimeFromTimeMaps(
        startTime,
        flatTimeMaps
      );

      if (!updatedStartTime) {
        setHasError(true);
        setIsLoading(false);
        updatedStartTime = "";
      }

      if (!availableSlots.includes(updatedStartTime)) {
        setHasError(true);
        setIsLoading(false);
      }

      const durationResult = getDurationConfig(
        allowedDurations,
        updatedStartTime,
        minutesDiff
      );

      if (!durationResult) {
        setHasError(true);
        setIsLoading(false);
      }

      const areConsecutiveSlotsValid = durationResult
        ? validateConsecutiveSlots(
            updatedStartTime,
            durationResult?.actualDuration || 0,
            availableSlots,
            occupiedSlots
          )
        : false;

      if (!areConsecutiveSlotsValid) {
        setHasError(true);
        setIsLoading(false);
      }

      const quotePayload = {
        bookingDate: selectedDate,
        locationId: slots?.locationId,
        selectedTime: updatedStartTime,
      };

      const quotaData = await fetchQuotaInfo(quotePayload);
      const selectedQuoteInfo = quotaData?.[resourceId] || {};

      const { isExceeded: isQuoteExceeded } = checkQuotaExceeded(
        selectedQuoteInfo?.quotaInfo
      );

      const advancedBookingResult = validateAdvancedBooking(
        selectedDate,
        updatedStartTime,
        selectResource?.policyInfo,
        t
      );

      const isBookable =
        !isQuoteExceeded &&
        advancedBookingResult.isValid &&
        !isEmpty(durationResult?.config) &&
        areConsecutiveSlotsValid &&
        availableSlots.includes(updatedStartTime);

      const selectedSlot: SingleSlot = {
        isBookable,
        resourceId,
        slot: {
          allowBringingGuest: selectResource.policyInfo?.allowBringGuest,
          availableForBooking: true,
          disable: !isBookable,
          ends: isBookable ? durationResult?.endTime : "",
          max: durationResult?.config?.max,
          min: durationResult?.config?.min,
          slot: isBookable ? durationResult?.actualDuration : 0,
          starts: updatedStartTime,
          type: slotType.TimeSlots,
        },
        tempEndTime: endTime,
        tempStartTime: startTime,
      };

      dispatch(bookingSlice.actions.changeSelectedTime(updatedStartTime));
      dispatch(
        drawerSlice.actions.setDrawerBookingData({
          resourceId,
          selectedTime: startTime,
          slot: isBookable ? durationResult?.actualDuration : 0,
        })
      );
      dispatch(bookingSlice.actions.changeSelectedSlot(selectedSlot));

      dispatch(
        drawerSlice.actions.setOpenPercent(DRAWER_HEIGHT.DEFAULT_OPEN_PERCENT)
      );

      return true;
    },
    [
      slots,
      selectedDate,
      fetchQuotaInfo,
      getSelectedResource,
      dispatch,
      setHasError,
      setIsLoading,
      timeMaps,
      t,
    ]
  );

  const handleTimeSelect = useCallback(
    async (selectInfo: DateSelectArg) => {
      const calendarApi = selectInfo.view.calendar;
      calendarApi.unselect();
      setHasError(false);
      setIsLoading(true);

      dispatch(bookingSlice.actions.changeSelectedTime(""));
      dispatch(bookingSlice.actions.changeSelectedSlot({} as SingleSlot));

      const startTime = moment(selectInfo.start).format(DateFormats.Time);
      const endTime = moment(selectInfo.end).format(DateFormats.Time);
      const minutesDiff = getMinutesDiff(
        selectInfo.startStr,
        selectInfo.endStr
      );
      const resourceId = selectInfo?.resource?.id ?? "";

      await updateSelectedSlot(startTime, endTime, resourceId, minutesDiff);

      dispatch(drawerSlice.actions.setShowBookingDrawer(true));

      setIsLoading(false);
    },
    [dispatch, updateSelectedSlot, setHasError, setIsLoading]
  );

  const handleEventResize = useCallback(
    async (resizeInfo: EventResizeDoneArg) => {
      const { event } = resizeInfo;

      if (event.extendedProps.type !== EventType.TempBooking) {
        return;
      }

      setIsLoading(true);

      const startTime = moment(event.start).format(DateFormats.Time);
      const endTime = moment(event.end).format(DateFormats.Time);
      const minutesDiff = getMinutesDiff(
        moment(event.start).format(DateFormats.YMDTime),
        moment(event.end).format(DateFormats.YMDTime)
      );
      const resourceId =
        event.getResources()[0]?.id || event.extendedProps.resourceId;

      await updateSelectedSlot(startTime, endTime, resourceId, minutesDiff);

      setIsLoading(false);
    },
    [dispatch, updateSelectedSlot, setIsLoading]
  );

  const handleEventDrop = useCallback(
    async (dropInfo: EventDropArg) => {
      const { event } = dropInfo;

      if (event.extendedProps.type !== EventType.TempBooking) {
        return;
      }

      setIsLoading(true);

      const startTime = moment(event.start).format(DateFormats.Time);
      const endTime = moment(event.end).format(DateFormats.Time);
      const minutesDiff = getMinutesDiff(
        moment(event.start).format(DateFormats.YMDTime),
        moment(event.end).format(DateFormats.YMDTime)
      );
      const resourceId =
        event.getResources()[0]?.id || event.extendedProps.resourceId;

      await updateSelectedSlot(startTime, endTime, resourceId, minutesDiff);

      setIsLoading(false);
    },
    [dispatch, updateSelectedSlot, setIsLoading]
  );

  const getEventClassName = (arg: EventContentArg) => {
    const type = arg.event.extendedProps.type as EventType;

    switch (type) {
      case EventType.Maintenance:
        return ["fg-event-maintenance"];
      case EventType.Blocking:
        return ["fg-event-blocking"];
      case EventType.Exception:
        return ["fg-event-maintenance"];
      case EventType.Restriction:
      case EventType.Unavailable:
        return ["fg-event-disabled"];
      case EventType.TempBooking:
        return ["fg-temp-event"];
      case EventType.Booking:
      default:
        return ["fg-event-booking"];
    }
  };

  return useMemo(
    () => ({
      getEventClassName,
      handleEventDrop,
      handleEventResize,
      handleTimeSelect,
    }),
    [getEventClassName, handleEventResize, handleTimeSelect, handleEventDrop]
  );
};
